/**
 * GfAnalysisSetupModal — the granny flat Suggestion Engine's "Strategy Analysis Setup" (owner,
 * 2 Oct 2026: the entry path into every module's engine asks the set-up questions that make the
 * tool worthwhile, the way aged care and the village do).
 *
 * The engine builds arrangements around the choices only the client can make, so each is asked
 * here, once, before the run — and only where a strategy would turn on it:
 *   - whether they already know what they will pay (and how much), or want the engine to suggest
 *     Centrelink's reasonable value so nothing counts as a gift;
 *   - what happens to the home: keep it (and whether it is rented out), sell it, sign it over for
 *     the right, or compare every route;
 *   - the state a bought investment property would be in, and the annuity willingness and share —
 *     only where selling the home would free enough to deploy;
 *   - the emergency reserve kept in the bank; how new money is invested; the pension cap.
 *
 * Mirrors the village and downsizer set-ups in shape and voice. Cancel runs on the defaults;
 * Continue saves the answers to the client record and the engine runs.
 */
import { villageInterestHeld, villageExitEntitlement, villageFeesAfterLeaving, todayIsoOf } from '../../../../shared/src/calculation/support/villageExit.ts';
import { villageMoveOutLine } from '../agedCare/villageExitWords.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import React, { useEffect, useMemo, useState } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS, type InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { STAMP_DUTY_STATES, isStampDutyState } from '../../../../shared/src/calculation/stampDuty.ts';
import { GENERAL_TRANSFER_BALANCE_CAP } from '../../constants/index.ts';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import type { AnnuityAppetite, AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import { DEFAULT_GF_ANNUITY_SHARE_CAP_PCT, GF_SETUP_SPEND_THRESHOLD, type GfEngineSetup, type GfHomeRoute } from '../../strategy/grannyFlatRecommendedStrategies.ts';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

export interface GfAnalysisSetupExtras {
    newMoneyProfile: InvestmentProfileId | null;
    tbcAmount: number | null;
}

export interface GfAnalysisSetupModalProps {
    isOpen: boolean;
    onClose: () => void;
    factFind: DraftFactFind;
    initial: GfEngineSetup;
    /** Centrelink's reasonable value of the right at the start date; null while it loads. */
    reasonableValue: number | null;
    homeValue: number;
    askNewMoney: boolean;
    tbcPerson: 'client' | 'partner' | null;
    /** Opens the family discussion guide (the modal closes first). */
    onOpenFamilyGuide?: () => void;
    onContinue: (setup: GfEngineSetup, extras: GfAnalysisSetupExtras) => void;
}

const clampPct = (v: unknown): number => {
    const n = Number(v);
    if (!Number.isFinite(n)) return DEFAULT_GF_ANNUITY_SHARE_CAP_PCT;
    return Math.min(100, Math.max(0, Math.round(n)));
};

const CHOICE_COLUMNS: Record<number, string> = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' };

function Choice<T extends string | number>({ options, value, onChange, ariaLabel }: { options: Array<{ id: T; label: string }>; value: T; onChange: (v: T) => void; ariaLabel: string }) {
    return (
        <div className={`grid grid-cols-1 ${CHOICE_COLUMNS[options.length] || 'sm:grid-cols-3'} gap-2`} role="radiogroup" aria-label={ariaLabel}>
            {options.map((o) => (
                <button
                    key={String(o.id)}
                    type="button"
                    role="radio"
                    aria-checked={value === o.id}
                    onClick={() => onChange(o.id)}
                    className={`rounded-lg border-2 px-3.5 py-2.5 text-sm font-semibold transition-all ${
                        value === o.id ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

const SectionIcon = ({ path }: { path: string }) => (
    <div className="p-2 bg-blue-100 rounded-lg shrink-0">
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
        </svg>
    </div>
);

const Section = ({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) => (
    <div className="bg-white border border-blue-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
            <SectionIcon path={icon} />
            <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-blue-800 mb-1">{title}</h3>
                {children}
            </div>
        </div>
    </div>
);

const ICON_HOME = 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6';
const ICON_COINS = 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
const ICON_TREND = 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6';
const ICON_LOCK = 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z';
const ICON_SHIELD = 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z';

const GfAnalysisSetupModal = ({ isOpen, onClose, factFind, initial, reasonableValue, homeValue, askNewMoney, tbcPerson, onOpenFamilyGuide, onContinue }: GfAnalysisSetupModalProps) => {
    const [knows, setKnows] = useState<'unknown' | 'known'>(Number(initial.knownAmount) > 0 ? 'known' : 'unknown');
    const [knownAmount, setKnownAmount] = useState<number | ''>(Number(initial.knownAmount) > 0 ? Number(initial.knownAmount) : '');
    // Cash is tested against the reasonable value; paying for the build is taken at face value (2 Oct 2026).
    const [knownKind, setKnownKind] = useState<'cash' | 'build'>(initial.knownAmountKind === 'build' ? 'build' : 'cash');
    const [knownExtraCash, setKnownExtraCash] = useState<number | ''>(Number(initial.knownExtraCash) > 0 ? Number(initial.knownExtraCash) : '');
    const [homeRoute, setHomeRoute] = useState<GfHomeRoute>(initial.homeRoute ?? 'all');
    const [keptHomeRent, setKeptHomeRent] = useState<number | ''>(Number(initial.keptHomeRent) > 0 ? Number(initial.keptHomeRent) : '');
    const [purchaseState, setPurchaseState] = useState<string>(initial.purchaseState || '');
    const [annuityCapPct, setAnnuityCapPct] = useState<number>(clampPct(initial.annuityShareCapPct ?? DEFAULT_GF_ANNUITY_SHARE_CAP_PCT));
    const [annuityAppetite, setAnnuityAppetite] = useState<AnnuityAppetite>(initial.annuityAppetite ?? 'unsure');
    const [annuityResidual, setAnnuityResidual] = useState<AnnuityResidual>(initial.annuityResidual ?? 'both');
    const [reserve, setReserve] = useState<number | ''>(Number(initial.emergencyReserve) > 0 ? Number(initial.emergencyReserve) : getRecommendedEmergencyFund(factFind));
    const [oldUnitPaysAtMove, setOldUnitPaysAtMove] = useState<boolean>(initial.oldUnitPaysAtMove !== false);
    const currentVillage = useMemo(() => {
        const ff = factFind as unknown as FactFind;
        if (!villageInterestHeld(ff)) return null;
        const today = todayIsoOf(new Date());
        const e = villageExitEntitlement(ff, today);
        return e ? { name: ff.client?.firstName || 'The client', ...e, feesAfterLeaving: villageFeesAfterLeaving(ff.retirementVillage, today, e.refundDate) } : null;
    }, [factFind]);
    const [newMoneyProfile, setNewMoneyProfile] = useState<InvestmentProfileId | ''>('');
    const [tbcAmount, setTbcAmount] = useState<number | ''>('');

    useEffect(() => {
        if (!isOpen) return;
        setKnows(Number(initial.knownAmount) > 0 ? 'known' : 'unknown');
        setKnownAmount(Number(initial.knownAmount) > 0 ? Number(initial.knownAmount) : '');
        setKnownKind(initial.knownAmountKind === 'build' ? 'build' : 'cash');
        setKnownExtraCash(Number(initial.knownExtraCash) > 0 ? Number(initial.knownExtraCash) : '');
        setHomeRoute(initial.homeRoute ?? 'all');
        setKeptHomeRent(Number(initial.keptHomeRent) > 0 ? Number(initial.keptHomeRent) : '');
        setPurchaseState(initial.purchaseState || '');
        setAnnuityCapPct(clampPct(initial.annuityShareCapPct ?? DEFAULT_GF_ANNUITY_SHARE_CAP_PCT));
        setAnnuityAppetite(initial.annuityAppetite ?? 'unsure');
        setAnnuityResidual(initial.annuityResidual ?? 'both');
        setReserve(Number(initial.emergencyReserve) > 0 ? Number(initial.emergencyReserve) : getRecommendedEmergencyFund(factFind));
        setNewMoneyProfile('');
        setTbcAmount('');
    }, [isOpen, initial.knownAmount, initial.knownAmountKind, initial.knownExtraCash, initial.homeRoute, initial.keptHomeRent, initial.purchaseState, initial.annuityShareCapPct, initial.annuityAppetite, initial.annuityResidual, initial.emergencyReserve, factFind]);

    if (!isOpen) return null;

    const hasHome = homeValue > 0;
    // Said only to a client who has one (owner, 2 Oct 2026): the face-value rule is for the home's
    // title; an investment property signed over is assets given, and tested.
    const hasInvestmentProperty = (factFind?.assets?.investmentProperty || []).some((p) => (parseFloat(String(p?.value ?? '')) || 0) > 0);
    const sells = hasHome && (homeRoute === 'sell' || homeRoute === 'all');
    const keeps = hasHome && (homeRoute === 'keep' || homeRoute === 'all');
    const fair = Math.round(reasonableValue ?? 0);
    const freedBySale = Math.max(0, Math.round(homeValue * 0.975) - fair);
    const spends = sells && freedBySale >= GF_SETUP_SPEND_THRESHOLD;
    const chosenState = isStampDutyState(purchaseState) ? STAMP_DUTY_STATES.find((s) => s.code === purchaseState) : undefined;
    const offerGuaranteed = guaranteedShapeAvailable(factFind as never);
    const guaranteeWindow = guaranteeWindowWords({
        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
        sex: factFind?.client?.sex,
        partnerAge: factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
        partnerSex: factFind?.partner?.sex,
        ownerName: factFind?.client?.firstName,
        partnerName: factFind?.partner?.firstName,
    });
    const annuityDollars = Math.round(freedBySale * (annuityCapPct / 100));
    const tbcName = (tbcPerson === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person';
    const amountMissing = knows === 'known' && !(Number(knownAmount) > 0);

    const handleContinue = () => {
        onContinue(
            {
                knownAmount: knows === 'known' && Number(knownAmount) > 0 ? Number(knownAmount) : 0,
                knownAmountKind: knows === 'known' ? knownKind : 'cash',
                knownExtraCash: knows === 'known' && knownKind === 'build' && Number(knownExtraCash) > 0 ? Number(knownExtraCash) : 0,
                homeRoute,
                keptHomeRent: Number(keptHomeRent) > 0 ? Number(keptHomeRent) : 0,
                purchaseState: chosenState ? chosenState.code : '',
                annuityShareCapPct: annuityCapPct,
                annuityAppetite,
                annuityResidual,
                emergencyReserve: Number(reserve) >= 0 ? Number(reserve) : getRecommendedEmergencyFund(factFind),
                oldUnitPaysAtMove,
            },
            { newMoneyProfile: newMoneyProfile === '' ? null : newMoneyProfile, tbcAmount: tbcAmount === '' ? null : Number(tbcAmount) },
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="gf-setup-title">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-teal-400 text-white p-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-10"></div>
                    <div className="relative z-10 flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h2 id="gf-setup-title" className="text-2xl font-bold mb-1">Strategy Analysis Setup</h2>
                                <p className="text-blue-100">A few choices the strategies turn on — yours to make, not the tool&apos;s</p>
                            </div>
                        </div>
                        <button type="button" onClick={onClose} aria-label="Close" className="text-white hover:text-blue-200 text-2xl leading-none transition-colors p-1 hover:bg-white hover:bg-opacity-10 rounded-full">
                            &times;
                        </button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(85vh-180px)]">
                    <div className="space-y-5">
                        {onOpenFamilyGuide && (
                            <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                                <strong>Has the family talked it through?</strong> A granny flat arrangement works only if everyone understands what is being given and what happens if things change.{' '}
                                <button type="button" onClick={onOpenFamilyGuide} className="font-semibold text-blue-700 hover:underline">Open the family discussion guide</button>
                            </p>
                        )}
                        {/* 1. What will be paid */}
                        <Section icon={ICON_SHIELD} title="What you will give for the right">
                            <p className="text-sm text-slate-700 mb-3">
                                Centrelink values a lifetime right to live with family at <strong>{reasonableValue != null ? formatCurrency(fair) : '…'}</strong> at your age. Pay cash up to that and nothing counts as a gift; pay more and the excess is counted for five years. Paying for the build, or signing over your home, is different: Centrelink takes those at face value, so none of it is a gift.{hasHome ? ' If you would sign your home over instead, choose that under "What happens to your home" below.' : ''}{hasInvestmentProperty ? ' An investment property is not your home: signed over, it is treated as assets given and tested like cash.' : ''}{' '}
                                <strong>Do you already know how much you&apos;ll pay?</strong>
                            </p>
                            <Choice<'unknown' | 'known'>
                                ariaLabel="Do you know how much you will pay"
                                value={knows}
                                onChange={setKnows}
                                options={[
                                    { id: 'unknown', label: 'No — suggest the right amount' },
                                    { id: 'known', label: 'Yes — I know the amount' },
                                ]}
                            />
                            {knows === 'known' && (
                                <>
                                    <p className="mt-4 mb-2 text-sm text-slate-700"><strong>How will it be given?</strong></p>
                                    <Choice<'cash' | 'build'>
                                        ariaLabel="How the amount will be given"
                                        value={knownKind}
                                        onChange={setKnownKind}
                                        options={[
                                            { id: 'cash', label: 'Cash to the family' },
                                            { id: 'build', label: 'Paying to build or extend their home' },
                                        ]}
                                    />
                                    <div className="mt-3 max-w-xs">
                                        <CurrencyInput label={knownKind === 'build' ? "Cost of the build (the builder's quote)" : 'Amount you plan to pay in cash'} value={knownAmount} onChange={(v) => setKnownAmount(v ?? '')} className="" />
                                        <p className={`text-xs mt-1 ${amountMissing ? 'text-amber-700' : 'text-gray-500'}`}>
                                            {amountMissing
                                                ? 'Enter the amount to continue.'
                                                : knownKind === 'build'
                                                    ? 'The cost of building your accommodation is the price of the right — no gift, whatever it costs. Keep the contract and invoices: anything paid beyond the actual build cost is treated as extra, and tested.'
                                                    : 'Modelled beside the no-gift option, with any gift flagged.'}
                                        </p>
                                    </div>
                                    {knownKind === 'build' && (
                                        <div className="mt-3 max-w-xs">
                                            <CurrencyInput label="Cash on top of the build, if any" value={knownExtraCash} onChange={(v) => setKnownExtraCash(v ?? '')} className="" placeholder="Leave blank for none" />
                                            <p className="text-xs text-gray-500 mt-1">Anything given on top of the build is tested against the greater of the build cost and {reasonableValue != null ? formatCurrency(fair) : 'the formula value'}; the excess is a gift.</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </Section>

                        {/* Moving from a retirement village (8 Oct 2026): the current unit's payout, stated here. */}
                        {currentVillage && (
                            <Section icon={ICON_HOME} title="Your current village">
                                <p className="text-sm text-slate-700 mb-3"><strong>When will the current unit be paid out?</strong></p>
                                <Choice<'move' | 'limit'>
                                    ariaLabel="When the current unit pays out"
                                    value={oldUnitPaysAtMove ? 'move' : 'limit'}
                                    onChange={(v) => setOldUnitPaysAtMove(v === 'move')}
                                    options={[
                                        { id: 'move', label: 'At the move' },
                                        { id: 'limit', label: `Later — up to ${currentVillage.refundMonths} months after leaving` },
                                    ]}
                                />
                                <p className="text-sm text-slate-700 mt-3" data-testid="gf-from-village">{villageMoveOutLine(currentVillage, 'granny_flat', { atMove: oldUnitPaysAtMove })}</p>
                                <p className="text-xs text-slate-500 mt-2">The terms come from the Client Profile (Living Arrangements). Change them there if they are not right.</p>
                            </Section>
                        )}
                        {/* 2. The home */}
                        {hasHome && (
                            <Section icon={ICON_HOME} title="What happens to your home">
                                <p className="text-sm text-slate-700 mb-3">
                                    Your {formatCurrency(homeValue)} home stops being exempt the day you move. <strong>What would you do with it?</strong> Keeping it means Centrelink counts it; selling frees the money but every dollar is counted; signing it over pays for the right with the home itself — taken at face value, so none of it is a gift, whatever the home is worth.
                                </p>
                                <Choice<GfHomeRoute>
                                    ariaLabel="What happens to your home"
                                    value={homeRoute}
                                    onChange={setHomeRoute}
                                    options={[
                                        { id: 'keep', label: 'Keep it' },
                                        { id: 'sell', label: 'Sell it' },
                                        { id: 'transfer', label: 'Sign it over' },
                                        { id: 'all', label: 'Compare all' },
                                    ]}
                                />
                                {keeps && (
                                    <div className="mt-4 max-w-xs">
                                        <CurrencyInput label="Rent if you let it out (a week)" value={keptHomeRent} onChange={(v) => setKeptHomeRent(v ?? '')} className="" placeholder="Leave blank to estimate" />
                                        <p className="text-xs text-gray-500 mt-1">The kept-and-rented version is modelled beside keeping it empty. Blank uses the rent on your profile, or about 3.5% of the home&apos;s value a year.</p>
                                    </div>
                                )}
                                {spends && (
                                    <div className="mt-4 max-w-xs">
                                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="gf-purchase-state">If a strategy buys an investment property, which state?</label>
                                        <select
                                            id="gf-purchase-state"
                                            value={chosenState ? chosenState.code : ''}
                                            onChange={(e) => setPurchaseState(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Not sure — leave stamp duty out</option>
                                            {STAMP_DUTY_STATES.map((s) => (
                                                <option key={s.code} value={s.code}>{s.name}</option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {chosenState
                                                ? `The duty is struck on ${chosenState.name}'s standard scale with no concessions, comes out of the proceeds, and the property is sized to what is left.`
                                                : 'With no state chosen no duty is charged, and every card that buys a property says so.'}
                                        </p>
                                    </div>
                                )}
                            </Section>
                        )}

                        {/* 3. Guaranteed income for life — only where a sale frees enough to lock part away */}
                        {spends && (
                            <Section icon={ICON_COINS} title="Guaranteed income for life">
                                <p className="text-sm text-slate-700 mb-3">
                                    Selling the home would free about <strong>{formatCurrency(freedBySale)}</strong> after the right is paid for. Some strategies lock part of it away with an insurer in exchange for a <strong>guaranteed income for life</strong> (a lifetime annuity).
                                    Centrelink counts only 60% of that money, which can lift the Age Pension. <strong>Should we include these options?</strong>
                                </p>
                                <Choice<AnnuityAppetite>
                                    ariaLabel="Should we include a lifetime annuity"
                                    value={annuityAppetite}
                                    onChange={setAnnuityAppetite}
                                    options={[
                                        { id: 'none', label: 'No — keep it all accessible' },
                                        { id: 'yes', label: 'Yes — include these options' },
                                    ]}
                                />
                                {annuityAppetite === 'yes' && (
                                    <>
                                        <p className="mt-4 mb-2 text-sm text-slate-700">
                                            <strong>Should any of it come back to the family on death?</strong> Guaranteeing more means a lower income.
                                            The full amount only comes back if death comes early{guaranteeWindow ? `, and ${guaranteeWindow}` : ''}; after that it falls away to nothing.
                                        </p>
                                        <Choice<AnnuityResidual>
                                            ariaLabel="Should the money come back to the family"
                                            value={annuityResidual}
                                            onChange={setAnnuityResidual}
                                            options={[
                                                { id: 'returned', label: 'Yes — as much as the rules allow' },
                                                ...(offerGuaranteed ? [{ id: 'guaranteed' as const, label: 'Yes — the full amount, whenever I die' }] : []),
                                                { id: 'nil', label: 'No — the highest income' },
                                                { id: 'both', label: 'Show me whichever works best' },
                                            ]}
                                        />
                                    </>
                                )}
                                {annuityAppetite !== 'none' && (
                                    <>
                                        <p className="mt-4 mb-2 text-sm text-slate-700"><strong>How much of the {formatCurrency(freedBySale)} freed up could be locked away, at most?</strong></p>
                                        <Choice<number>
                                            ariaLabel="How much could be locked away at most"
                                            value={annuityCapPct}
                                            onChange={setAnnuityCapPct}
                                            options={[
                                                { id: 10, label: 'A small part (10%)' },
                                                { id: 25, label: 'A quarter (25%)' },
                                                { id: 50, label: 'Half (50%)' },
                                                { id: 100, label: 'All of it (100%)' },
                                                ...([10, 25, 50, 100].includes(annuityCapPct) ? [] : [{ id: annuityCapPct, label: `${annuityCapPct}% (as set)` }]),
                                            ]}
                                        />
                                        <p className="text-xs text-gray-500 mt-2">
                                            = <span className="font-semibold tabular-nums">{formatCurrency(annuityDollars)}</span>. The rest stays invested and accessible.{' '}
                                            {annuityDollars < 25_000
                                                ? 'Under $25,000 is too small to be worth modelling, so no such strategy will be offered at this setting.'
                                                : annuityAppetite === 'unsure' || annuityResidual === 'both'
                                                    ? 'Both versions are modelled: as much coming back as the rules allow (a lower income), and nothing coming back (a higher income).'
                                                    : annuityResidual === 'returned'
                                                        ? 'Only the version where the money comes back is modelled.'
                                                        : 'Only the higher-income version, where nothing comes back, is modelled.'}
                                        </p>
                                    </>
                                )}
                                {annuityAppetite === 'none' && (
                                    <p className="text-xs text-gray-500 mt-2">No strategy that locks money away for life will be offered.</p>
                                )}
                            </Section>
                        )}

                        {/* 4. The reserve */}
                        <Section icon={ICON_LOCK} title="Emergency funds to keep aside">
                            <p className="text-sm text-slate-700 mb-3">
                                Left in the bank rather than paid across or invested — a buffer for unexpected costs. The default is six months of living expenses or {formatCurrency(getRecommendedEmergencyFund(factFind))}, whichever is more; keep more if you prefer.
                            </p>
                            <div className="max-w-xs">
                                <CurrencyInput label="Keep in the bank" value={reserve} onChange={(v) => setReserve(v ?? '')} className="" />
                            </div>
                        </Section>

                        {/* 5. How new money is invested — the question every engine asks once */}
                        {askNewMoney && (
                            <Section icon={ICON_TREND} title="Investing freed-up money">
                                <p className="text-sm text-slate-700 mb-3">
                                    Some strategies invest what a sale frees. <strong>How would that money be invested?</strong> Leave it and the tool assumes Conservative, the most cautious option, and asks again next time.
                                </p>
                                <select
                                    aria-label="How new money would be invested"
                                    value={newMoneyProfile}
                                    onChange={(e) => setNewMoneyProfile(e.target.value as InvestmentProfileId | '')}
                                    className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Not sure — assume Conservative (most cautious)</option>
                                    {INVESTMENT_PROFILE_IDS.map((id) => (
                                        <option key={id} value={id}>{INVESTMENT_PROFILES[id].label}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-2">Saved to the client profile, so you&apos;ll only be asked once.</p>
                            </Section>
                        )}

                        {/* 6. The transfer balance cap — only when the $1.6m screen trips */}
                        {tbcPerson && (
                            <Section icon={ICON_LOCK} title={`${tbcName}'s pension cap`}>
                                <p className="text-sm text-slate-700 mb-3">
                                    The government limits how much any one person may move into a retirement pension over their lifetime — currently{' '}
                                    <strong>{formatCurrency(GENERAL_TRANSFER_BALANCE_CAP)}</strong> — and {tbcName}&apos;s balances are large enough that it may now cap how much more can go in.
                                    Roughly how much has {tbcName} moved into retirement-phase pensions so far? Leave it blank and the tool estimates from current pension balances, and the advice document says so.
                                </p>
                                <div className="max-w-xs">
                                    <CurrencyInput label="Lifetime amount moved into pensions ($)" name="gfTbcAmount" value={tbcAmount} onChange={(v) => setTbcAmount(v ?? '')} />
                                </div>
                            </Section>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                    <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-semibold">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={amountMissing}
                        className="px-6 py-2 bg-gradient-to-r from-blue-500 to-teal-400 text-white rounded-lg hover:from-blue-600 hover:to-teal-500 transition-all duration-200 font-semibold shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Continue to Analysis
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GfAnalysisSetupModal;
